import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserCircle, Camera, KeyRound, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function avatarUrl(fileUrl) {
  return fileUrl ? `${API_BASE.replace(/\/api$/, "")}${fileUrl}` : "";
}

export function Avatar({ user, size = 40, className = "", "data-testid": testid }) {
  const initials = (user?.name || "?")
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, "")[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
  const src = user?.avatar_url ? avatarUrl(user.avatar_url) : null;
  return (
    <div
      data-testid={testid}
      className={`inline-flex items-center justify-center rounded-full bg-brand-emerald text-white font-semibold overflow-hidden shrink-0 ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {src ? <img src={src} alt={user.name} className="w-full h-full object-cover" /> : initials}
    </div>
  );
}

export default function Perfil() {
  const { user, refresh, logout } = useAuth();
  const [form, setForm] = useState({ name: "", title: "", phone: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [pwdDialog, setPwdDialog] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        title: user.title || "",
        phone: user.phone || "",
        bio: user.bio || "",
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      toast.success("Perfil actualizado");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen no debe exceder 2 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Avatar actualizado");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir el avatar");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    if (!user?.avatar_url) return;
    if (!window.confirm("¿Eliminar tu foto de perfil?")) return;
    try {
      await api.delete("/auth/avatar");
      toast.success("Avatar eliminado");
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    }
  };

  const changePassword = async () => {
    if (newPwd.length < 8) return toast.error("La nueva contraseña debe tener al menos 8 caracteres");
    if (newPwd !== confirmPwd) return toast.error("Las contraseñas no coinciden");
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd, new_password: newPwd,
      });
      toast.success("Contraseña actualizada. Vuelva a iniciar sesión.");
      setPwdDialog(false);
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      // Cierre forzado de sesión — se marca en localStorage para mostrar aviso en Login.
      localStorage.setItem("fstudy_pwd_changed", "1");
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setChangingPwd(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl space-y-8 animate-fade-in-up" data-testid="perfil-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <UserCircle className="h-8 w-8 text-brand-emerald" />
          Mi Perfil
        </h1>
        <p className="text-sm text-stone-500 mt-2">
          Su información profesional visible en el equipo, auditoría y consumo de créditos.
        </p>
      </div>

      <div className="card-elevated p-8">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="flex flex-col items-center gap-3">
            <Avatar user={user} size={128} data-testid="perfil-avatar" />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                   onChange={onUpload} className="hidden" data-testid="perfil-avatar-input" />
            <div className="flex gap-2">
              <Button
                type="button" size="sm" variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="perfil-upload-avatar"
              >
                {uploading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Subiendo…</>
                           : <><Camera className="h-3 w-3 mr-1" /> Cambiar foto</>}
              </Button>
              {user.avatar_url && (
                <Button
                  type="button" size="sm" variant="ghost"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={removeAvatar}
                  data-testid="perfil-remove-avatar"
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Quitar
                </Button>
              )}
            </div>
            <p className="text-[10px] text-stone-400 text-center max-w-[180px]">
              PNG, JPG o WEBP · Máx 2 MB
            </p>
          </div>

          <form onSubmit={saveProfile} className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Nombre completo</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="perfil-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Correo (solo lectura)</Label>
              <Input value={user.email} disabled className="bg-stone-50" data-testid="perfil-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Cargo / Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Ej: Directora Creativa" data-testid="perfil-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Teléfono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     placeholder="+504 ..." data-testid="perfil-phone" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Bio corta</Label>
              <Textarea rows={3} value={form.bio}
                        onChange={(e) => setForm({ ...form, bio: e.target.value })}
                        placeholder="Una línea que lo describe profesionalmente…"
                        maxLength={500} data-testid="perfil-bio" />
            </div>
            <div className="md:col-span-2 flex justify-between pt-2">
              <Button type="button" variant="outline"
                      onClick={() => setPwdDialog(true)}
                      data-testid="perfil-open-password">
                <KeyRound className="h-4 w-4 mr-2" /> Cambiar contraseña
              </Button>
              <Button type="submit" disabled={saving}
                      className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark hover:text-white shadow-gold"
                      data-testid="perfil-save">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Guardando…" : "Guardar perfil"}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Dialog open={pwdDialog} onOpenChange={setPwdDialog}>
        <DialogContent data-testid="perfil-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Cambiar contraseña</DialogTitle>
            <DialogDescription>
              Mínimo 8 caracteres. Confirme la nueva contraseña.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Contraseña actual</Label>
              <Input type="password" value={currentPwd}
                     onChange={(e) => setCurrentPwd(e.target.value)}
                     data-testid="perfil-pwd-current" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Nueva contraseña</Label>
              <Input type="password" minLength={8} value={newPwd}
                     onChange={(e) => setNewPwd(e.target.value)}
                     data-testid="perfil-pwd-new" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-600 font-semibold">Confirmar nueva contraseña</Label>
              <Input type="password" minLength={8} value={confirmPwd}
                     onChange={(e) => setConfirmPwd(e.target.value)}
                     data-testid="perfil-pwd-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialog(false)}>Cancelar</Button>
            <Button onClick={changePassword} disabled={changingPwd}
                    className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
                    data-testid="perfil-pwd-submit">
              {changingPwd ? "…" : "Cambiar contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
